import { Alert, AlertDescription } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { CircleAlert, ChevronRight, Loader2, ChevronLeft } from 'lucide-react';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { BalanceReferenceCard } from '@/components/payments/BalanceReferenceCard';
import type { BalanceReferenceDto } from '@/lib/connect-api';
import { PaymentMethodSelectionSimple } from '@/components/payments/PaymentMethodSelectionSimple';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { formatMoneyInLanguage } from '@/lib/currency';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { useTranslation } from '@/hooks/useTranslation';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';

interface PaymentMethodTabProps {
  amount: number;
  currency?: string;
  paymentMethod: PaymentMethod;
  onSelectMethod: (method: PaymentMethod) => void;
  onBack: () => void;
  onProcessPayment: () => void;
  isProcessing: boolean;
  error: string | null;
  // Email is collected on the amount step; this only gates the CTA as a backstop.
  emailValid: boolean;
  /** Restrict the offered methods (e.g. card/SEPA only for scheduled payments). */
  availableMethods?: PaymentMethod[];
  /** Methods in the list that aren't yet selectable, with the reason shown on their row. */
  disabledReasons?: Partial<Record<PaymentMethod, string>>;
  /** Formatted date for a scheduled payment; flips the CTA to "Pay … on {date}". */
  scheduledDateLabel?: string;
  /** Whether the account's shared code may be served for the payment this page is
   *  about to take — the owning page's `shouldServeSharedReference` verdict, which
   *  also decides whether the charge reuses the code. The card and the charge must
   *  answer to the same value: the code is bound to the undiscounted account total,
   *  so showing it beside one debt of several, or beside a discounted price, hands
   *  the customer a payable code for the wrong amount. Required rather than
   *  defaulted — a case-level surface must answer this, not inherit an assumption. */
  canServeSharedReference: boolean;
  /** The account's held code, read once by the owning page so this tab never
   *  fetches it itself. Null when there is none to show. */
  balanceReference?: BalanceReferenceDto | null;
  excludesArrangedDebts?: boolean;
  /** A live case code for exactly this part payment's amount, when Multibanco is chosen for it.
   *  The owning page decides, on the same predicate that routes the charge to the case mint. */
  heldCaseReference?: BalanceReferenceDto | null;
}

export function PaymentMethodTab({
  amount,
  currency = 'EUR',
  paymentMethod,
  onSelectMethod,
  onBack,
  onProcessPayment,
  isProcessing,
  error,
  emailValid,
  availableMethods,
  disabledReasons,
  scheduledDateLabel,
  canServeSharedReference,
  balanceReference = null,
  excludesArrangedDebts = false,
  heldCaseReference = null,
}: PaymentMethodTabProps) {
  const { t, currentLanguage } = useTranslation();
  const { data: methodConfigAvailability } = usePaymentMethodAvailability();
  const isSelectedMethodUsable =
    (!availableMethods || availableMethods.includes(paymentMethod)) && methodConfigAvailability[paymentMethod] && !disabledReasons?.[paymentMethod];

  return (
    <div className="w-full h-full stack items-center mt-4">
      <div className="w-full max-w-lg mx-auto h-full flex flex-col px-2 xs:px-4 space-y-6">
        {/* Payment Method Selection */}
        <div className="flex gap-4 items-center ">
          {/* Back Button */}
          <Button variant="subtle" size="icon-xs" onClick={onBack} className="rounded-full">
            <ChevronLeft />
          </Button>
          <h2 className="text-lg font-semibold">{t('paymentMethod.selectMethod')}</h2>
        </div>

        {/* Payment Options */}
        <PaymentMethodSelectionSimple
          selectedMethod={paymentMethod}
          onSelect={onSelectMethod}
          disabled={isProcessing}
          availableMethods={availableMethods}
          disabledReasons={disabledReasons}
        />

        {/* A Multibanco code the customer already holds. The page has already
            decided this payment may reuse it, method included. */}
        {canServeSharedReference && <BalanceReferenceCard reference={balanceReference} excludesArrangedDebts={excludesArrangedDebts} />}
        {heldCaseReference && <BalanceReferenceCard reference={heldCaseReference} />}

        {/* Payment Amount Display */}
        <div className="flex items-center justify-center mx-auto flex-col bg-soft border rounded-2xl px-4 py-2 max-w-lg w-full">
          <div className="text-sm text-muted-foreground">{t('paymentTabs.totalBalance')}</div>
          <CurrencyDisplay amount={amount} currency={currency} className="text-2xl font-semibold" centsClassName="text-lg" />
          {scheduledDateLabel && (
            <div className="text-sm font-medium">
              {t('paymentTabs.scheduledForDate', { date: scheduledDateLabel }).replace('{{date}}', scheduledDateLabel)}
            </div>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="max-w-lg mx-auto">
            <CircleAlert />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Continue Button */}
      </div>
      <BottomActionBar>
        <Button
          onClick={onProcessPayment}
          size="xl"
          className="group w-full max-w-sm"
          disabled={isProcessing || !emailValid || !isSelectedMethodUsable}
        >
          {isProcessing ? (
            <>
              <Loader2 className="size-5 mr-2 animate-spin" />
              {t('verify.processing')}
            </>
          ) : (
            <>
              {scheduledDateLabel
                ? t('paymentTabs.schedulePayment')
                : t('paymentTabs.payNow', { amount: formatMoneyInLanguage(amount, currentLanguage, currency) })}
              <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </Button>
      </BottomActionBar>
    </div>
  );
}
